import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { useGetProfessionalRulesetForContractQuery } from "store/queries/professionalRuleset";
import styled from "styled-components";

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FieldLabel = styled.div`
  font-weight: 600;
  color: var(--gray-800);
`;

const FieldHelp = styled.div`
  font-size: 0.8rem;
  color: var(--gray-600);
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Note = styled.div`
  font-size: 0.85rem;
  color: var(--gray-600);
  margin-top: 0.5rem;
`;

interface IProfessionalRulesetCardProps {
  providerId: string;
  payerId: number;
}

export function ProfessionalRulesetCard({
  providerId,
  payerId,
}: IProfessionalRulesetCardProps) {
  const { data, isLoading } = useGetProfessionalRulesetForContractQuery({
    providerId,
    payerId,
  });

  const ruleset = data?.data ?? null;
  const compoundBilateralAndMppr = ruleset?.compoundBilateralAndMppr ?? true;
  const endoApplies = ruleset?.endoApplies ?? true;
  const disabledFamilyCount = ruleset?.disabledEndoBaseCptCodes?.length ?? 0;

  return (
    <Card title="Professional Ruleset" className="mb-4">
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <>
          <Grid>
            <Field>
              <FieldLabel>Compound Bilateral + MPPR</FieldLabel>
              <ToggleRow>
                {compoundBilateralAndMppr ? (
                  <Tag value="On" severity="info" />
                ) : (
                  <Tag value="Off" severity="warning" />
                )}
              </ToggleRow>
              <FieldHelp>
                When off, MPPR does not stack on top of bilateral discounts.
              </FieldHelp>
            </Field>
            <Field>
              <FieldLabel>Endoscopy Rules Apply</FieldLabel>
              <ToggleRow>
                {endoApplies ? (
                  <Tag value="On" severity="info" />
                ) : (
                  <Tag value="Off" severity="warning" />
                )}
              </ToggleRow>
              <FieldHelp>
                When off, endoscopy adjustments are skipped for this provider
                and payer.
                {endoApplies && disabledFamilyCount > 0
                  ? ` ${disabledFamilyCount} ${
                      disabledFamilyCount === 1 ? "family" : "families"
                    } turned off.`
                  : ""}
              </FieldHelp>
            </Field>
          </Grid>
          {!ruleset && (
            <Note>
              No Professional ruleset configured. Defaults shown above mirror
              current engine behavior. Configure on the Payer Rules →
              Professional Rules tab.
            </Note>
          )}
        </>
      )}
    </Card>
  );
}

export default ProfessionalRulesetCard;
